import { takeEvery, takeLatest } from 'redux-saga'
import { call, put, take, fork, select, race, cancel, all } from 'redux-saga/effects'

import actionTypes from 'redux/actions/actionTypes'
import pollActions from 'redux/actions/pollActions'
import { pollSelectors } from 'redux/selectors'
import { pollSync, iconSync, slideSync } from 'sync'
import fileUploader from 'helpers/fileUploader'
import callAny from './helpers/call_any'
import { localize } from 'helpers/translator'
import errorCodes from 'helpers/enums/errorCodes'
import iconTypes from 'helpers/enums/iconTypes'
import formatError from './helpers/error_formatter'

function *pollDeleteReferenceMedia(action) {
	try {
		const questionSelector = yield select(pollSelectors.pollQuestionSelector(action.questionId))
		yield put(pollActions.deleteReferenceMediaSuccess(action.questionId))
		if (questionSelector.icon.status === 'uploading') {
			yield callAny(iconSync.cancelUpload, questionSelector.icon.uploadId)
		}

	} catch (error) {
	}
}

function *pollDeleteAnswerValidation(action) {
	try {
		const deleteAnswerValidationSelector = yield select(pollSelectors.deleteAnswerValidationSelector(action.questionId))
		if (deleteAnswerValidationSelector.valid) {
			yield put(pollActions.deleteAnswerValidationSuccess(action.questionId, action.answerId))
		} else {
			yield put(pollActions.deleteAnswerValidationError(deleteAnswerValidationSelector.message))
		}
	} catch (error) {
		// console.log(error)
	}
}

function *pollCancelChanges() {
	const cancelSelector = yield select(pollSelectors.pollCancelSelector)
	if (cancelSelector.isModified) {
		yield put(pollActions.cancelPollConfirm())
	} else {
		yield put(pollActions.cancelPollConfirmed())
	}
}

function *pollCreateInitialize(action) {
	try {
		const response = yield callAny(pollSync.getPoll, action.pollUID, action.jwt)
		yield put(pollActions.initializePollSuccess(response.payload.poll))
	} catch (error) {
		yield put(pollActions.initializePollError())
	}
}

function *pollSavePoll() {
	const successActions = [
		actionTypes.POLL_SAVE_POLL_SUCCESS,
		actionTypes.POLL_QUESTION_UPDATE_SUCCESS,
		actionTypes.POLL_DELETE_QUESTION_SUCCESS,
		actionTypes.POLL_QUESTION_SAVE_SUCCESS,
		actionTypes.POLL_UPLOADER_UPLOAD_SUCCESS,
	]
	const errorActions = [
		actionTypes.POLL_UPLOADER_VALIDATE_ERROR,
		actionTypes.POLL_UPLOAD_REFERENCE_MEDIA_ERROR,
		actionTypes.POLL_UPLOADER_UPLOAD_ERROR,
		actionTypes.POLL_UPLOADER_VALIDATE_CONTENT_ERROR,
		actionTypes.POLL_QUESTION_UPDATE_ERROR,
	]
	let saveSelector
	try {
		const validationSelector = yield select(pollSelectors.pollValidationSelector)
		if (validationSelector.valid) {
			saveSelector = yield select(pollSelectors.saveSelector)
			yield put(pollActions.validationSuccess(saveSelector.allIconsDone ? null : localize('poll_will_save_after_uploading')))

			// Checks if there is any question that has changed and was not saved
			// Then for each one it dispatches a new action to save the question
			const questionsChanged = yield select(pollSelectors.questionsChangedSelector)
			yield all(questionsChanged.map(question => put(pollActions.questionSave(question.id))))

			let allRequestsDone = false
			while (!allRequestsDone) {
				saveSelector = yield select(pollSelectors.saveSelector)
				allRequestsDone = saveSelector.allQuestionsDone && saveSelector.allIconsDone

				if (allRequestsDone) {
					const response = yield callAny(pollSync.savePoll, saveSelector.entityUID)
					yield put(pollActions.savePollSuccess(response.payload.redirectUrl, response.payload.metrics))
				} else {
					const requestActions = yield take([...successActions, ...errorActions])
					if (errorActions.includes(requestActions.type)) {
						allRequestsDone = true
						throw requestActions.error || new Error('Save Undefined Error')
					}
				}
			}
		} else {
			yield put(pollActions.validationError(validationSelector.message))
		}
	} catch (error) {
		yield put(pollActions.savePollError(error))
	}
}

function *pollSaveSettings(action) {
	try {
		const settingsSelector = yield select(pollSelectors.pollSettingsSelector)

		yield callAny(pollSync.updateSettings, settingsSelector.entityUID, action.settings)
		yield put(pollActions.savePollSettingsSuccess(localize('poll_save_settings_success')))
	} catch (error) {
		yield put(pollActions.savePollSettingsError(localize('global_generic_error')))
	}
}

function *takeQuestionOrIconDeletionWithId(questionId) {
	let isDeleted = false
	while (!isDeleted) {
		const action = yield take([actionTypes.POLL_DELETE_QUESTION_SUCCESS, actionTypes.POLL_DELETE_REFERENCE_MEDIA])
		if (action.questionId === questionId) {
			isDeleted = true
		}
	}
}

function *waitForQuestionCreation(questionId) {
	let hasQuestionId = false
	let questionSelector
	while (!hasQuestionId) {
		questionSelector = yield select(pollSelectors.pollQuestionSelector(questionId))
		if (!questionSelector.serverId) {
			const questionActions = yield take([actionTypes.POLL_QUESTION_SAVE_SUCCESS,
				actionTypes.POLL_QUESTION_SAVE_ERROR,
				actionTypes.POLL_DELETE_QUESTION_SUCCESS])
			if (questionActions.questionId === questionId && questionActions.type !== actionTypes.POLL_QUESTION_SAVE_SUCCESS) {
				throw new Error()
			}
		} else {
			hasQuestionId = true
		}
	}
	return questionSelector
}

function *pollAddAnswerValidation(action) {
	try {
		const addAnswerValidationSelector = yield select(pollSelectors.addAnswerValidationSelector(action.questionId))
		if (addAnswerValidationSelector.valid) {
			yield put({
				type: actionTypes.POLL_ADD_ANSWER_VALIDATION_SUCCESS,
				questionId: action.questionId,
			})
		} else {
			yield put({
				type: actionTypes.POLL_ADD_ANSWER_VALIDATION_ERROR,
				message: addAnswerValidationSelector.message,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *pollUploadReferenceMedia(action) {
	try {
		let questionSelector = yield select(pollSelectors.pollQuestionSelector(action.questionId))
		const validateResponse = yield callAny(iconSync.validateFilesPicked, action.allowedTypes, action.files['files'], questionSelector.presentationId, questionSelector.entityUID, action.force)
		const payload = validateResponse.payload.icons[0]
		const file = action.files['files'][0]
		if (payload.error_code === 0) {
			yield put(pollActions.uploaderValidateSuccess(action.questionId, payload.uploadId))
			questionSelector = yield call(waitForQuestionCreation, action.questionId)
			const {uploadResponse} = yield race({
				questionOrIconDeleted: call(takeQuestionOrIconDeletionWithId, action.questionId),
				uploadResponse: callAny(fileUploader, payload.uploadId, file, 'Question', questionSelector.serverId, iconTypes.ENTITY_ICON_TYPE_REFERENCE_MEDIA, action.allowedTypes, questionSelector.presentationId, questionSelector.entityUID, action.force),
			})
			if (uploadResponse) {
				yield put(pollActions.uploaderUploadSuccess(action.questionId, uploadResponse.payload.icon))
			}
		} else {
			// webpage content validation
			if (payload.error_code === errorCodes.ICON_INVALID_CONTENT_INTEGRITY) {
				yield put(pollActions.uploaderValidateContentError(payload.message, action.questionId))
				yield take(actionTypes.POLL_UPLOAD_REQUIRE_CONFIRMATION)
				yield put(pollActions.uploadReferenceMedia(action.allowedTypes, action.files, action.questionId, action.featureType, true))
			} else {
				yield put(pollActions.uploaderValidateError(payload.message, action.questionId))
			}
		}
	} catch (error) {
		let message = localize('quiz_service_error_message')
		if (error.serverError && error.serverError.message) {
			message = error.serverError.message
		}
		yield put(pollActions.uploaderUploadError(error.serverError, message, action.questionId))
	}
}

function *pollCreateUpdateQuestion(action) {
	try {
		// Not necesary now
		//yield call(delay, 2000)
		const questionSelector = yield call(waitForQuestionCreation, action.questionId)
		yield callAny(pollSync.updateQuestion, questionSelector.text, questionSelector.entityUID, questionSelector.icon.id, questionSelector.answers)
		yield put(pollActions.questionUpdateSuccess(action.questionId))
	} catch (error) {
		const serverError = formatError(error)
		yield put(pollActions.questionUpdateError(serverError))
	}
}

function *pollTakeLatestCreateUpdateQuestion() {
	//this is a customized take latest for updating questions
	//to avoid server concurrency we use the delay
	//and to avoid missing updates for different questions, we compare the last one with the previous one
	let questionId = 0
	let previousFork = null
	let action
	while (true) {
		action = yield take([actionTypes.POLL_QUESTION_SAVE,
			actionTypes.POLL_ANSWER_SAVE,
			actionTypes.POLL_DELETE_ANSWER_VALIDATION_SUCCESS,
			actionTypes.POLL_DROP_ANSWER,
			actionTypes.POLL_ADD_ANSWER_VALIDATION_SUCCESS])
		if (questionId === action.questionId && previousFork !== null) {
			yield cancel(previousFork)
		}
		questionId = action.questionId
		previousFork = yield fork(pollCreateUpdateQuestion, action)
	}
}

function *timerSave(action) {
	try {
		const { timerValue, entityUID } = yield select(pollSelectors.pollTimerSelector)
		yield callAny(slideSync.saveTimer, timerValue, entityUID)
		yield put({
			type: actionTypes.POLL_TIMER_SAVE_SUCCESS,
		})
	} catch (error) {
		yield put({
			type: actionTypes.POLL_TIMER_SAVE_ERROR,
			previous_timer: action.previous_timer,
		})
	}
}

export default [
	takeLatest(actionTypes.POLL_ADD_ANSWER, pollAddAnswerValidation),
	takeLatest(actionTypes.POLL_CANCEL_POLL, pollCancelChanges),
	takeLatest(actionTypes.POLL_DELETE_ANSWER, pollDeleteAnswerValidation),
	takeLatest([
		actionTypes.POLL_INITIALIZE_CREATION_TOOL,
		actionTypes.POLL_SCREENSHOT_INITIALIZE_ROUTE,
		actionTypes.POLL_INITIALIZE_STANDALONE,
	], pollCreateInitialize),
	takeLatest(actionTypes.POLL_SAVE_POLL, pollSavePoll),
	takeLatest(actionTypes.POLL_SAVE_SETTINGS, pollSaveSettings),
	fork(pollTakeLatestCreateUpdateQuestion),
	takeEvery(actionTypes.POLL_UPLOAD_REFERENCE_MEDIA, pollUploadReferenceMedia),
	takeLatest(actionTypes.POLL_DELETE_REFERENCE_MEDIA, pollDeleteReferenceMedia),
	takeEvery(actionTypes.POLL_DELETE_REFERENCE_MEDIA_SUCCESS, pollCreateUpdateQuestion),
	takeLatest([
		actionTypes.POLL_SAVE_TIMER,
		actionTypes.POLL_DELETE_TIMER,
	], timerSave),
]

export {
	pollAddAnswerValidation,
	pollCancelChanges,
	pollDeleteAnswerValidation,
	pollCreateInitialize,
	pollSavePoll,
	pollSaveSettings,
	pollTakeLatestCreateUpdateQuestion,
	pollUploadReferenceMedia,
	takeQuestionOrIconDeletionWithId,
	waitForQuestionCreation,
	pollDeleteReferenceMedia,
	pollCreateUpdateQuestion,
}
